<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                  Enrollment View                                     │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div class="rewards-enrollment" v-if="loaded">

        <template v-if="enrollmentSuccess != null">
            <div class="completion-message">
                <template v-if="enrollmentSuccess == true">
                    <p>Congratulations! Your enrollment has been approved.</p>
                    <p>An email has been sent to <b class="email">{{ email }}</b>.</p>
                    <p>Please click the link provided to activate your account.</p>
                </template>
                <template v-else>
                    <p>We were unable to process your enrollment at this time.</p>
                    <p>Please try again later.</p>

                    <button @click="enrollmentSuccess = null">Try Again</button>
                </template>
            </div>
        </template>

        <form @submit.prevent="onSubmit" novalidate v-else>

            <h3 class="rewards-title">Enrollment Info</h3>

            <!-- NAME -->
            <div class="member-id">

                <div class="full-name">
                    <!-- Title -->
                    <div class="labeled-select-input title-menu">
                        <label :class="{'required-field':titleIsRequired}">Title</label>
                        <select v-model="title" :class="{'required-field':titleIsRequired}">
                            <option :value="null">Title</option>
                            <option value="Mr">Mr</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Ms">Ms</option>
                            <option value="Mstr">Mstr</option>
                            <option value="Miss">Miss</option>
                        </select>
                        <span v-if="errorDict.Title" class="error">{{ errorDict.Title }}</span>
                    </div>

                    <!-- First Name -->
                    <labeled-text-input name="firstName" id="firstName" :class="[{'required-field':firstNameIsRequired}, 'first-name']" v-model="firstName" has-icon>
                        First Name
                        <template #addendum>
                            <span v-if="errorDict.FirstName" class="error">{{ errorDict.FirstName }}</span>
                        </template>
                    </labeled-text-input>

                    <!-- Last Name -->
                    <labeled-text-input name="lastName" id="lastName" :class="[{'required-field':lastNameIsRequired}, 'last-name']" v-model="lastName">
                        Last Name
                        <template #addendum>
                            <span v-if="errorDict.LastName" class="error">{{ errorDict.LastName }}</span>
                        </template>
                    </labeled-text-input>
                </div>

            </div>

            <!-- Date of birth -->
            <template>
                <div class="labeled-select-input date-of-birth">

                    <label>Date of birth</label>
                    <div class="dob-day">
                        <select v-model="dateOfBirthDay" :class="[{'required-field':dayIsRequired}, 'day']">
                            <option :value="null">Day</option>
                            <option :value="day" v-for="day in dobDateDays">{{ day }}</option>
                        </select>
                        <span v-if="errorDict.DateOfBirthDay" class="error">{{ errorDict.DateOfBirthDay }}</span>
                    </div>

                    <div class="dob-month">
                        <select v-model="dateOfBirthMonth" :class="[{'required-field':monthIsRequired}, 'month']">
                            <option :value="null">Month</option>
                            <option :value="idx" v-for="(month, idx) in dobDateMonths">{{ month }}</option>
                        </select>
                        <span v-if="errorDict.DateOfBirthMonth" class="error">{{ errorDict.DateOfBirthMonth }}</span>
                    </div>

                    <div class="dob-year">
                        <select v-model="dateOfBirthYear" :class="[{'required-field':yearIsRequired}, 'year']">
                            <option :value="null">Year</option>
                            <option :value="year" v-for="year in dobDateYears">{{ year }}</option>
                        </select>
                        <span v-if="errorDict.DateOfBirthYear" class="error">{{ errorDict.DateOfBirthYear }}</span>
                    </div>
                </div>
                <span v-if="errorDict.MinAge" class="error">{{ errorDict.MinAge }}</span>

            </template>

            <!-- CONTACT -->
            <div class="contact-info">

                <!-- Email -->
                <div class="email-input-container">
                    <labeled-text-input name="email" id="memberEmail" :class="[{'required-field':emailIsRequired}, 'member-email']" type="email"
                                        v-model="email" has-icon autocomplete="new-email">
                        Email
                        <template #addendum>
                            <span v-if="errorDict.Email" class="error">{{ errorDict.Email }}</span>
                            <span v-if="errorDict.EmailTooLong" class="error">{{ errorDict.EmailTooLong }}</span>
                        </template>
                    </labeled-text-input>
                </div>

                <!-- Phone -->
                <div class="member-phone">

                    <!-- Country Code -->
                    <div class="labeled-select-input country-code">
                        <label>Phone Country Code</label>
                        <select v-model="phoneCountryCode">
                            <option :value="null">Select a country</option>
                            <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.code">
                                {{ countryCode.title }} {{ countryCode.code ? `(+${countryCode.code})` : '' }}
                            </option>
                        </select>
                    </div>

                    <!-- Number -->
                    <div>
                        <labeled-text-input name="phoneNumber" id="phoneNumber" class="phone-input" v-model="phoneNumber" has-icon>
                            Phone
                            <template #addendum>
                                <span v-if="errorDict.PhoneNumber" class="error">{{ errorDict.PhoneNumber }}</span>
                                <span v-if="errorDict.PhoneNumberTooLong" class="error">{{ errorDict.PhoneNumberTooLong }}</span>
                            </template>
                        </labeled-text-input>
                    </div>

                    <!-- Type -->
                    <div class="labeled-select-input phone-type">
                        <label>Type</label>
                        <select v-model="phoneType">
                            <option :value="null">Type</option>
                            <option value="H">Home</option>
                            <option value="B">Business</option>
                            <option value="M">Mobile</option>
                        </select>
                    </div>
                </div>
            </div>

            <template v-if="CONFIG.RewardsProgram.ExtendedEnrollmentFields">
                <h5>Mailing Address</h5>

                <!-- ADDRESS -->
                <div class="mailing-address">

                    <!-- Street -->
                    <labeled-text-input name="addressOne" id="addressOne" class="address line-1" v-model="addressLineOne">
                        Address Line 1
                        <template #addendum>
                            <span v-if="errorDict.AddressLineOne" class="error">{{ errorDict.AddressLineOne }}</span>
                            <span v-if="errorDict.AddressLineOneTooLong" class="error">{{ errorDict.AddressLineOneTooLong }}</span>
                        </template>
                    </labeled-text-input>

                    <labeled-text-input name="addressTwo" id="addressTwo" class="address line-2" v-model="addressLineTwo">
                        Address Line 2
                        <template #addendum>
                            <span v-if="errorDict.AddressLineTwoTooLong" class="error">{{ errorDict.AddressLineTwoTooLong }}</span>
                        </template>
                    </labeled-text-input>

                    <!-- City -->
                    <labeled-text-input name="city" id="city" class="address-city" v-model="addressCity">
                        City
                        <template #addendum>
                            <span v-if="errorDict.AddressCity" class="error">{{ errorDict.AddressCity }}</span>
                            <span v-if="errorDict.AddressCityTooLong" class="error">{{ errorDict.AddressCityTooLong }}</span>
                        </template>
                    </labeled-text-input>

                    <!-- State/Province -->
                    <labeled-text-input name="stateProvince" id="stateProvince" class="address-state" v-model="addressState">
                        State/Province
                        <template #addendum>
                            <span v-if="errorDict.AddressState" class="error">{{ errorDict.AddressState }}</span>
                            <span v-if="errorDict.AddressStateTooLong" class="error">{{ errorDict.AddressStateTooLong }}</span>
                        </template>
                    </labeled-text-input>

                    <!-- Postal -->
                    <labeled-text-input name="postalCode" id="postalCode" class="address-postal" v-model="addressZip">
                        Postal Code
                        <template #addendum>
                            <span v-if="errorDict.AddressZip" class="error">{{ errorDict.AddressZip }}</span>
                            <span v-if="errorDict.AddressZipTooLong" class="error">{{ errorDict.AddressZipTooLong }}</span>
                        </template>
                    </labeled-text-input>

                    <!-- Country -->
                    <div class="address-country labeled-select-input">
                        <label>Country</label>
                        <select v-model="addressCountryCode">
                            <option :value="null">Select a country</option>
                            <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.country">{{ countryCode.title }}</option>
                        </select>
                    </div>
                </div>

                <h5 :class="{'required-field':natIdPassportIsRequired}">
                    <template v-if="CONFIG.AirlineName && CONFIG.AirlineNicName">
                        Please enter {{ CONFIG.AirlineName }} {{ CONFIG.AirlineNicName }} Number or Passport
                    </template>
                    <template v-else>
                        Please enter National ID Number or Passport
                    </template>
                </h5>

                <span v-if="errorDict.NatIdPassport" class="error">{{ errorDict.NatIdPassport }}</span>
                <span v-if="errorDict.NatIdPassportTooLong" class="error">{{ errorDict.NatIdPassportTooLong }}</span>

                <div class="id-passport">

                    <div class="national-id">

                        <!--National ID-->
                        <labeled-text-input name="nationalId" id="nationalId" class="member-document" v-model="nationalIdNumber">
                            <template v-if="CONFIG.AirlineName && CONFIG.AirlineNicName">
                                {{ CONFIG.AirlineName }} {{ CONFIG.AirlineNicName }} Number
                            </template>
                            <template v-else>
                                National ID Number
                            </template>
                        </labeled-text-input>
                    </div>

                    <div class="passport">

                        <!--Passport Number-->
                        <labeled-text-input name="passport" id="passport" class="number member-document has-icon" v-model="passportNumber">
                            Passport Number
                        </labeled-text-input>
                        <span v-if="errorDict.Passport" class="error">{{ errorDict.Passport }}</span>
                        <span v-if="errorDict.PassportNumberTooLong" class="error">{{ errorDict.PassportNumberTooLong }}</span>

                        <!--Expiration Date-->
                        <template v-if="CONFIG.RewardsProgram.ExtendedEnrollmentFields">
                            <div class="labeled-select-input passport-expires">

                                <label>Expires</label>

                                <div class="expires-day">
                                    <select v-model="passportExpiresDay" class="day">
                                        <option :value="null">Day</option>
                                        <option :value="day" v-for="day in expiryDateDays">{{ day }}</option>
                                    </select>
                                </div>

                                <div class="expires-month">
                                    <select v-model="passportExpiresMonth" class="month">
                                        <option :value="null">Month</option>
                                        <option :value="idx" v-for="(month, idx) in expiryDateMonths">{{ month }}</option>
                                    </select>
                                </div>

                                <div class="expires-year">
                                    <select v-model="passportExpiresYear" class="year">
                                        <option :value="null">Year</option>
                                        <option :value="year" v-for="year in expiryDateYears">{{ year }}</option>
                                    </select>
                                </div>
                            </div>
                        </template>

                        <!-- Issuing Country -->
                        <div class="issuing-country labeled-select-input">
                            <label>Issuing Country</label>
                            <select v-model="passportIssuingCountry">
                                <option :value="null">Select a country</option>
                                <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.country">{{ countryCode.title }}</option>
                            </select>
                            <span v-if="errorDict.PassportIssuingCountry" class="error">{{ errorDict.PassportIssuingCountry }}</span>
                        </div>

                        <!--Nationality-->
                        <div class="nationality labeled-select-input">
                            <label>Nationality</label>
                            <select v-model="passportNationality">
                                <option :value="null">Select a Nationality</option>
                                <option :value="country.country" v-for="country in countryCodes">{{ country.title }}</option>
                            </select>
                            <span v-if="errorDict.PassportNationality" class="error">{{ errorDict.PassportNationality }}</span>

                        </div>

                    </div>

                </div>
            </template>

            <!-- TERMS AND CONDITIONS -->
            <label class="terms-and-conditions">
                <input type="checkbox" name="terms" v-model="termsAndConditions" />
                <span>I accept all <a :href="CONFIG.RewardsProgram.TermsURL" target="_blank">rules, terms and conditions</a> of {{ TEXT.REWARDS_PROGRAM }}</span>
            </label>

            <!-- CONFIRM DUPLICATE EMAIL -->
            <label class="allow-duplicate-email" v-if="showDuplicateEmailCheckbox">
                <input type="checkbox" v-model="allowDuplicateEmailAddress" />
                <span>I want to re-use this email address: <strong>{{ email }}</strong></span>
            </label>

            <!-- FORM SUBMIT -->
            <div class="user-action">
                <button class="button-s" type="submit" v-bind:disabled="!termsAndConditions">Enroll</button>
            </div>

        </form>

        <transition-expand v-if="error || exception" classList="conflicts-container">
            <conflicts :error.sync="error" :exception.sync="exception" :is-sys-admin="isSysAdmin"></conflicts>
        </transition-expand>
    </div>
</template>

<script>
    import { fetchJSON, postJSON } from "@/helpers/fetch"
    import { isoDate, toDate } from "@/helpers/dates"
    import conflicts, { CONFLICTS } from "@/common/conflicts"

    export default {
        components: { conflicts },
        mixins: [CONFLICTS],

        emits: ['loading', 'loaded', 'submitting', 'submitted', 'close'],

        props: {
            portalInfo: Object
        },

        name: 'EnrollmentView',
        data() {
            return {

                title: null,
                firstName: null,
                lastName: null,
                dateOfBirth: null,
                email: null,

                phoneCountryCode: null,
                phoneNumber: null,
                phoneType: 'M',

                addressLineOne: null,
                addressLineTwo: null,
                addressCity: null,
                addressState: null,
                addressZip: null,
                addressCountryCode: null,

                dobDay: null,
                dobMonth: null,
                dobYear: null,

                nationalIdNumber: null,
                nationalIdIssuingCountry: null,

                passportNumber: null,
                passportExpires: null,
                passportIssuingCountry: null,
                passportNationality: null,

                expiryDay: null,
                expiryMonth: null,
                expiryYear: null,

                countryCodes: [],
                termsAndConditions: false,

                errorDict: {},
                loaded: false,

                enrollmentSuccess: null,    //  null: hasn't tried enrolling yet; true: successful enrollment; false: problem with enrollment

                showDuplicateEmailCheckbox: false,
                allowDuplicateEmailAddress: false,

                submitting: false,
            }
        },

        mounted() {
            this.$emit('loading')

            this.getCountryCodes()
                .then(() => {
                    this.loaded = true
                    this.$emit('loaded')
                })
        },

        watch: {
            enrollmentSuccess() {
                this.clearError()
            }
        },

        methods: {
            checkForErrors() {
                let validate = this.fieldsToValidate

                //Validation
                this.errorDict = {};

                if (validate.name) {
                    if (!this.title) {
                        this.errorDict["Title"] = "Title is required";
                    }
                    if (!this.firstName) {
                        this.errorDict["FirstName"] = "First name is required";
                    }
                    if (!this.lastName) {
                        this.errorDict["LastName"] = "Last name is required";
                    }

                    if (this.firstName && this.firstName.length > 40) {
                        this.errorDict["FirstNameTooLong"] = "First name cannot exceed 40 characters in length"
                    }

                    if (this.lastName && this.lastName.length > 50) {
                        this.errorDict["LastNameTooLong"] = "Last name cannot exceed 50 characters in length"
                    }
                }

                if (validate.email) {
                    if (!this.email) {
                        this.errorDict["Email"] = "Email is required";
                    } else if (!/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(this.email)) {
                        this.errorDict["Email"] = "Invalid Email";
                    }
                    if (this.email && this.email.length > 50) {
                        this.errorDict["EmailTooLong"] = "Email address cannot exceed 50 characters in length";
                    }
                }


                if (validate.dateOfBirth) {
                    if (!this.dateOfBirthDay || this.dateOfBirthDay == "Day") {
                        this.errorDict["DateOfBirthDay"] = 'Day is required';
                    }
                    if (this.dateOfBirthMonth == null || this.dateOfBirthMonth == "Month") {
                        this.errorDict["DateOfBirthMonth"] = 'Month is required';
                    }
                    if (!this.dateOfBirthYear || this.dateOfBirthYear == "Year") {
                        this.errorDict["DateOfBirthYear"] = 'Year is required';
                    }
                }

                if (validate.phone) {
                    //Phone Country Code Validation
                    const specialCharactersCheck = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
                    const lettersCheck = /[A-za-z]/;

                    if (this.phoneNumber) {
                        if (specialCharactersCheck.test(this.phoneNumber) && lettersCheck.test(this.phoneNumber)) {
                            this.errorDict["PhoneNumber"] = "Phone Number must not contain any letters or special characters";
                        } else if (specialCharactersCheck.test(this.phoneNumber)) {
                            this.errorDict["PhoneNumber"] = "Phone Number must not contain any special characters";
                        } else if (lettersCheck.test(this.phoneNumber)) {
                            this.errorDict["PhoneNumber"] = "Phone Number must not contain any letters";
                        }

                        if (this.phoneNumber.length > 20) {
                            this.errorDict["PhoneNumberTooLong"] = "Phone Number cannot exceed 20 characters in length";
                        }
                    }
                }


                if (validate.idDocument) {
                    //National ID
                    if (!this.nationalIdNumber && !this.passportNumber) {
                        this.errorDict["NatIdPassport"] = "Please enter a valid National ID or Passport";
                    }

                    if (this.nationalIdNumber && this.nationalIdNumber.length > 20) {
                        this.errorDict["NatIdPassportTooLong"] = "National ID Number cannot exceed 20 characters in length"
                    }

                    //Passport
                    if (this.passportNumber && !this.passportIssuingCountry && !this.passportNationality) {
                        this.errorDict["PassportNationality"] = "Nationality is required when using a Passport.";
                        this.errorDict["PassportIssuingCountry"] = "Issuing Country is required when using a Passport";
                    } else if (this.passportNumber && !this.passportIssuingCountry && this.passportNationality) {
                        this.errorDict["PassportIssuingCountry"] = "Issuing Country is required when using a Passport";
                    } else if (this.passportNumber && this.passportIssuingCountry && !this.passportNationality) {
                        this.errorDict["PassportNationality"] = "Nationality is required when using a Passport";
                    }

                    if (this.passportNumber && this.passportNumber.length > 20) {
                        this.errorDict["PassportNumberTooLong"] = "Passport Number cannot exceed 20 characters in length";
                    }
                }

                if (validate.address) {
                    //Address Validation
                    if (!this.addressLineOne && this.addressLineTwo) {
                        this.addressLineOne = this.addressLineTwo;
                        this.addressLineTwo = '';
                    }

                    if (this.addressLineOne && this.addressLineOne.length > 75) {
                        this.errorDict["AddressLineOneTooLong"] = "Address Line One cannot exceed 75 characters in length";
                    }

                    if (this.addressLineTwo && this.addressLineTwo.length > 50) {
                        this.errorDict["AddressLineTwoTooLong"] = "Address Line Two cannot exceed 50 characters in length";
                    }

                    if (this.addressCity && this.addressCity.length > 40) {
                        this.errorDict["AddressCityTooLong"] = "City cannot exceed 40 characters in length";
                    }

                    if (this.addressState && this.addressState.length > 40) {
                        this.errorDict["AddressStateTooLong"] = "State/Province cannot exceed 40 characters in length";
                    }

                    if (this.addressZip && this.addressZip.length > 15) {
                        this.errorDict["AddressZipTooLong"] = "Postal Code cannot exceed 15 characters in length";
                    }

                    if (this.addressLineOne || this.addressCity || this.addressState || this.addressZip) {

                        const addressFields = {
                            AddressLineOne: this.addressLineOne,
                            AddressCity: this.addressCity,
                            AddressState: this.addressState,
                            AddressZip: this.addressZip
                        }

                        const errorMessages = {
                            AddressLineOne: "Address cannot be left blank",
                            AddressCity: "City cannot be left blank",
                            AddressState: "State cannot be left blank",
                            AddressZip: "Postal code cannot be left blank"
                        }

                        for (var [key, value] of Object.entries(addressFields)) {
                            if (value == null || value == '') {
                                this.errorDict[key] = errorMessages[key];
                            }
                        }
                    }
                }

                //Minimum Age Requirement
                if (this.calcAge() < this.CONFIG.RewardsProgram.MinimumAge) {
                    if (this.CONFIG.RewardsProgram.MinimumAge) {
                        this.errorDict["MinAge"] = 'You must be ' + this.CONFIG.RewardsProgram.MinimumAge + ' years of age to enroll';
                    }
                }
            },

            onSubmit() {
                this.checkForErrors()

                let errors = Object.values(this.errorDict)
                if (errors.length) {
                    alert(["Please fix the following errors:","", ...errors].join('\n'))
                }
                else if (this.submitting) {
                    alert('Enrollment in process; please wait ...')
                }
                else {
                    this.$emit('submitting')
                    this.submitting = true

                    postJSON(`/api/members/CreateMember`, {
                        member: this.memberUpdate,
                        allowDuplicateEmailAddress: this.allowDuplicateEmailAddress,
                    })
                        .then(() => {
                            this.enrollmentSuccess = true
                        })
                        .catch(error => {
                            let conflicts = ((error || {}).error || {}).conflicts || []
                            if (conflicts.filter(c => c.code == 802).length) {
                                this.showDuplicateEmailCheckbox = this.portalInfo.allowEmailReuse
                                if (this.showDuplicateEmailCheckbox)
                                    return alert("Duplicate email address. Please confirm if you wish to reuse.")
                                else
                                    return alert("Email address already in use. Use Forgot Password action if needed.")
                            }

                            this.onThrow(error)
                            this.enrollmentSuccess = false
                        })
                        .finally(() => {
                            this.$emit('submitted')
                            this.submitting = false
                        })
                }
            },

            getCountryCodes() {
                return fetchJSON(`/api/members/GetPhoneCountryCodes`)
                    .then(data => {
                        this.countryCodes = data.countryCodes;
                        let airlineCountryTitle = (this.CONFIG.AirlineCountry || "").toUpperCase()

                        let airlineCountry = (this.countryCodes || []).find(code => code.title == airlineCountryTitle)
                        if (airlineCountry) {
                            let { code: phoneCountryCode, country: airlineCountryCode } = airlineCountry

                            //  Pre-set the phone country code so the user doesn't think they have to do it themselves
                            //  (We could wait until they enter their phone number but that would make them wonder)
                            if (!this.phoneCountryCode) {
                                this.phoneCountryCode = phoneCountryCode
                            }

                            //  If entering ID documents is required, pre-set the country code for national ID
                            if (this.fieldsToValidate.idDocument) {
                                if (!this.nationalIdIssuingCountry) {
                                    this.nationalIdIssuingCountry = airlineCountryCode
                                }
                                if (airlineCountryCode) {
                                    if (!this.passportIssuingCountry) {
                                        this.passportIssuingCountry = airlineCountryCode
                                    }
                                    if (!this.passportNationality) {
                                        this.passportNationality = airlineCountryCode
                                    }
                                }
                            }
                        }
                    })
                    .catch(this.onThrow)
            },

            calcAge() {
                if (this.dateOfBirth) {
                    let birthday = this.dateOfBirth;

                    const today = new Date();
                    const difference = new Date(today.valueOf() - birthday.valueOf());
                    return Math.abs(difference.getFullYear() - 1970);
                }
            },
        },

        computed: {

            fieldsToValidate() {
                //  Minimum fields to validate
                let fieldsToValidate = {
                    name: true,
                    dateOfBirth: true,
                    email: true,
                }

                let extendedEnrollmentFields = this.CONFIG.RewardsProgram.ExtendedEnrollmentFields
                if (extendedEnrollmentFields === true)
                    fieldsToValidate = {
                        ...fieldsToValidate,

                        phoneNumber: true,
                        idDocument: true, //    either a passport OR a nationalId must be provided
                        address: true,
                    }
                else if (extendedEnrollmentFields instanceof Object && extendedEnrollmentFields.constructor.name == 'Object')
                    fieldsToValidate = {
                        ...fieldsToValidate,
                        ...extendedEnrollmentFields,
                    }

                return fieldsToValidate
            },

            titleIsRequired() {
                if (this.title) {
                    this.errorDict["Title"] = '';
                    return false;
                }
                return true;
            },

            firstNameIsRequired() {
                if (this.firstName) {
                    this.errorDict["FirstName"] = '';
                    return false;
                }
                return true;
            },

            lastNameIsRequired() {
                if (this.lastName) {
                    this.errorDict["LastName"] = '';
                    return false;
                }
                return true;
            },

            dayIsRequired() {
                if (this.dateOfBirthDay) {
                    this.errorDict["DateOfBirthDay"] = '';
                    return false;
                }
                return true;
            },

            monthIsRequired() {
                //DOB Month starts at index 0 which will be recongized as falsy
                if (this.dateOfBirthMonth || this.dateOfBirthMonth == 0) {
                    this.errorDict["DateOfBirthMonth"] = '';
                    return false;
                }
                return true;
            },

            yearIsRequired() {
                if (this.dateOfBirthYear) {
                    this.errorDict["DateOfBirthYear"] = '';
                    return false;
                }
                return true;
            },

            emailIsRequired() {
                if (!this.email) {
                    return true;
                } else if (!/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(this.email)) {
                    return true;
                }
                return false;
            },

            natIdPassportIsRequired() {
                if (this.nationalIdNumber || this.passportNumber) {
                    return false;
                }
                return true;
            },

            memberUpdate() {
                return {
                    title: this.title,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    dateOfBirth: isoDate(this.dateOfBirth),
                    email: this.email,
                    address: {
                        address1: this.addressLineOne,
                        address2: this.addressLineTwo,
                        city: this.addressCity,
                        countryCode: this.addressCountryCode,
                        state: this.addressState,
                        zip: this.addressZip
                    },
                    phone: {
                        number: this.phoneNumber,
                        countryCode: this.phoneCountryCode,
                        type: this.phoneType
                    },
                    nationalId: {
                        number: this.nationalIdNumber,
                        issuingCountry: this.nationalIdIssuingCountry
                    },
                    passport: {
                        number: this.passportNumber,
                        expires: isoDate(this.passportExpires),
                        issuingCountry: this.passportIssuingCountry,
                        nationality: this.passportNationality
                    },
                    password: this.password,
                }
            },

            dateOfBirthDay: {
                get() {
                    if (this.dateOfBirth) return this.dateOfBirth.getDate();
                    if (this.dobDay) return this.dobDay;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.dateOfBirth = null;
                        this.dobDay = null;
                    } else {
                        if (this.dateOfBirth) this.dateOfBirth = new Date(this.dateOfBirth.setDate(value));
                        let currentYear = new Date().getFullYear();

                        this.dobDay = value;

                        if (!this.dateOfBirth) {
                            if (this.dobDay && this.dobMonth != null && this.dobYear) this.dateOfBirth = new Date(`${this.dobMonth} ${value}, ${currentYear}`);
                        }
                    }
                }
            },
            dateOfBirthMonth: {
                get() {
                    if (this.dateOfBirth) return this.dateOfBirth.getMonth();
                    if (this.dobMonth !== null) return this.dobMonth;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.dateOfBirth = null;
                        this.dobMonth = null;
                    } else {
                        if (this.dateOfBirth) this.dateOfBirth = new Date(this.dateOfBirth.setMonth(value));
                        let currentYear = new Date().getFullYear();
                        let monthVal = this.dobDateMonths[value];

                        this.dobMonth = value;

                        if (!this.dateOfBirth) {
                            if (this.dobDay && this.dobMonth !== null && this.dobYear) this.dateOfBirth = new Date(`${monthVal} ${this.dobDay}, ${currentYear}`);
                        }
                    }
                }
            },
            dateOfBirthYear: {
                get() {
                    if (this.dateOfBirth) return this.dateOfBirth.getFullYear();
                    if (this.dobYear) return this.dobYear;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.dateOfBirth = null;
                        this.dobYear = null;
                    } else {

                        this.dobYear = value;

                        if (this.dateOfBirth) this.dateOfBirth = new Date(this.dateOfBirth.setFullYear(value));
                        if (!this.dateOfBirth) {
                            if (this.dobDay && this.dobMonth !== null && this.dobYear) this.dateOfBirth = new Date(`${this.dobMonth + 1} ${this.dobDay}, ${value}`);
                        }
                    }
                }
            },

            passportExpiresDay: {
                get() {
                    if (this.passportExpires) return this.passportExpires.getDate();
                    if (this.expiryDay) return this.expiryDay;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.passportExpires = null;
                        this.expiryDay = null;
                    } else {
                        if (this.passportExpires) this.passportExpires = new Date(this.passportExpires.setDate(value));
                        let currentYear = new Date().getFullYear();

                        this.expiryDay = value;

                        if (!this.passportExpires) {
                            if (this.expiryDay && this.expiryMonth && this.expiryYear) this.passportExpires = new Date(`${this.expiryMonth} ${value}, ${currentYear}`);
                        }
                    }
                }
            },
            passportExpiresMonth: {
                get() {
                    if (this.passportExpires) return this.passportExpires.getMonth();
                    if (this.expiryMonth) return this.expiryMonth;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.passportExpires = null;
                        this.expiryMonth = null;
                    } else {
                        if (this.passportExpires) this.passportExpires = new Date(this.passportExpires.setMonth(value));
                        let currentYear = new Date().getFullYear();
                        let monthVal = this.expiryDateMonths[value];

                        this.expiryMonth = value;

                        if (!this.passportExpires) {
                            if (this.expiryDay && this.expiryMonth && this.expiryYear) this.passportExpires = new Date(`${monthVal} ${this.expiryDay}, ${currentYear}`);
                        }
                    }
                }
            },
            passportExpiresYear: {
                get() {
                    if (this.passportExpires) return this.passportExpires.getFullYear();
                    if (this.expiryYear) return this.expiryYear;
                    return null;
                },
                set(value) {
                    if (value === null) {
                        this.passportExpires = null;
                        this.expiryYear = null;
                    } else {
                        if (this.passportExpires) this.passportExpires = new Date(this.passportExpires.setFullYear(value));

                        this.expiryYear = value;

                        if (!this.passportExpires) {
                            if (this.expiryDay && this.expiryMonth && this.expiryYear) this.passportExpires = new Date(`${this.expiryMonth} ${this.expiryDay}, ${value}`);
                        }
                    }
                }
            },

            //Date of Birth Dates

            dobDateDays() {
                //Create a list of dates (1 -> 31)
                return Array.from({ length: 31 }, (_, i) => i + 1);
            },

            dobDateMonths() {
                //Create a list of Months (Jan -> Dec)
                return Array.from(Array(12), (v, i) => new Date(0, i).toLocaleString({}, { month: 'short' }));
            },

            dobDateYears() {
                //Create a list of Years (CurrentYear - 120 -> CurrentYear)
                const currentYear = new Date().getFullYear();

                const range = (start, stop, next) => Array.from({
                    length: (stop - start) / next + 1
                }, (_, i) => start + (i * next));

                return range(currentYear, currentYear - 120, -1);
            },

            //Passport Expiration Dates

            expiryDateDays() {
                //Create a list of dates (1 -> 31)
                return Array.from({ length: 31 }, (_, i) => i + 1);
            },

            expiryDateMonths() {
                //Create a list of Months (Jan -> Dec)
                return Array.from(Array(12), (v, i) => new Date(0, i).toLocaleString({}, { month: 'short' }));
            },

            expiryDateYears() {
                //Create a list of Years (CurrentYear - 120 -> CurrentYear)
                const currentYear = new Date().getFullYear();

                const range = (start, stop, next) => Array.from({
                    length: (stop - start) / next + 1
                }, (_, i) => start + (i * next));

                return range(currentYear, currentYear + 20, 1);
            },
        },
    }
</script>
